import { useMemo, useState } from "react";
import {
  Box,
  Chip,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Close";
import TuneIcon from "@mui/icons-material/Tune";

/**
 * Combined search + chip filter row.
 *
 * Props:
 *  - search:        controlled search value
 *  - onSearchChange: (val) => void
 *  - placeholder:   search input placeholder
 *  - filters:       array of { id, label, value, options: [{value, label}], multi? }
 *                   Each is rendered as a horizontally scrollable chip group when set.
 *  - onFilterChange:(id, nextValue) => void
 *  - actions:       optional right-side ReactNode (e.g. "Create" button)
 *  - dense:         tighter vertical padding for nested usage
 */
export default function FilterBar({
  search,
  onSearchChange,
  placeholder = "Search…",
  filters = [],
  onFilterChange,
  actions,
  dense = false,
  sx,
}) {
  const [showFilters, setShowFilters] = useState(false);

  const hasFilters = filters.length > 0;
  const activeCount = useMemo(
    () =>
      filters.reduce((n, f) => {
        if (Array.isArray(f.value)) return n + f.value.length;
        return n + (f.value ? 1 : 0);
      }, 0),
    [filters],
  );

  return (
    <Box
      sx={{
        py: dense ? 1.5 : 2,
        px: { xs: 1.5, sm: 2 },
        bgcolor: "background.paper",
        borderBottom: 1,
        borderColor: "divider",
        ...sx,
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.5}
        alignItems={{ md: "center" }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ flex: 1, minWidth: 0 }}
        >
          {onSearchChange && (
            <TextField
              size="small"
              placeholder={placeholder}
              value={search ?? ""}
              onChange={(e) => onSearchChange(e.target.value)}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      fontSize="small"
                      sx={{ color: "text.secondary" }}
                    />
                  </InputAdornment>
                ),
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => onSearchChange("")}
                      aria-label="Clear search"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{ maxWidth: { md: 380 } }}
            />
          )}
          {hasFilters && (
            <IconButton
              size="small"
              onClick={() => setShowFilters((v) => !v)}
              aria-label="Toggle filters"
              sx={{
                display: { md: "none" },
                color: activeCount ? "primary.main" : "text.secondary",
              }}
            >
              <TuneIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
        {actions && (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ flexShrink: 0 }}
          >
            {actions}
          </Stack>
        )}
      </Stack>

      {hasFilters && (
        <Stack
          spacing={1}
          sx={{
            mt: 1.5,
            display: { xs: showFilters ? "flex" : "none", md: "flex" },
          }}
        >
          {filters.map((filter) => (
            <FilterRow
              key={filter.id}
              filter={filter}
              onChange={(next) => onFilterChange?.(filter.id, next)}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
}

function FilterRow({ filter, onChange }) {
  const { label, value, options, multi } = filter;
  const isActive = (optValue) => {
    if (multi && Array.isArray(value)) return value.includes(optValue);
    return value === optValue;
  };
  const toggle = (optValue) => {
    if (multi) {
      const current = Array.isArray(value) ? value : [];
      const next = current.includes(optValue)
        ? current.filter((v) => v !== optValue)
        : [...current, optValue];
      onChange(next);
      return;
    }
    onChange(value === optValue ? null : optValue);
  };

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{ overflowX: "auto", scrollbarWidth: "none" }}
    >
      {label && (
        <Box
          sx={{
            fontSize: 12,
            fontWeight: 600,
            color: "text.secondary",
            flexShrink: 0,
          }}
        >
          {label}
        </Box>
      )}
      <Stack direction="row" spacing={0.75}>
        {options.map((opt) => {
          const active = isActive(opt.value);
          return (
            <Chip
              key={opt.value ?? "__all"}
              label={opt.label}
              onClick={() => toggle(opt.value)}
              size="small"
              variant={active ? "filled" : "outlined"}
              color={active ? "primary" : "default"}
              sx={{
                fontWeight: 500,
                ...(active && {
                  color: "primary.contrastText",
                  bgcolor: "primary.main",
                }),
              }}
            />
          );
        })}
      </Stack>
    </Stack>
  );
}
